import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../App';
import { getCurrencySettings, updateCurrencySettings } from '../services/currencySettingsService';
import { resolvePlanFeatures } from '../lib/planFeatures';
import { FEATURE_BY_ID } from '../lib/featureCatalog';
import { 
  UserIcon, 
  SettingsIcon, 
  CreditCardIcon, 
  BuildingIcon,
  MapPinIcon,
  PhoneIcon,
  GlobeIcon,
  BriefcaseIcon,
  UsersIcon,
  CalendarIcon,
  AlertCircleIcon,
  CheckCircleIcon,
  EditIcon,
  SaveIcon,
  XIcon,
  PackageIcon,
  ReceiptIcon,
  ArrowUpIcon,
  CheckIcon,
  LockIcon,
  Loader2Icon,
  TrashIcon,
  ShieldIcon,
  InfoIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GeneralSettings = ({ user, type, isOnboarding = false }) => {
  const { checkAuth } = useAuth();
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isChangeRequestModalOpen, setIsChangeRequestModalOpen] = useState(false);
  const [changeRequestData, setChangeRequestData] = useState({
    full_name: '',
    email: '',
    role_id: ''
  });
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Preferred UI language (TR/EN)
  const [preferredLanguage, setPreferredLanguage] = useState(user?.preferred_language || 'tr');
  const [savingLanguage, setSavingLanguage] = useState(false);

  useEffect(() => {
    if (user?.preferred_language) {
      setPreferredLanguage(user.preferred_language);
    }
  }, [user?.preferred_language]);

  const handleSaveLanguage = async (lang) => {
    if (lang === user?.preferred_language) {
      return;
    }
    try {
      setSavingLanguage(true);
      await axios.patch(
        `${API}/auth/preferences/language`,
        { preferred_language: lang },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setPreferredLanguage(lang);
      toast.success(lang === 'en' ? 'Language updated' : 'Dil tercihi güncellendi');
      if (checkAuth) await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dil tercihi kaydedilemedi');
    } finally {
      setSavingLanguage(false);
    }
  };

  // Load roles when modal opens
  useEffect(() => {
    if (isChangeRequestModalOpen) {
      loadAvailableRoles();
    }
  }, [isChangeRequestModalOpen]);

  const loadAvailableRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await axios.get(`${API}/roles`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAvailableRoles(response.data || []);
      console.log('Loaded roles:', response.data);
    } catch (error) {
      console.error('Error loading roles:', error);
      toast.error('Roller yüklenemedi');
    } finally {
      setLoadingRoles(false);
    }
  };
  const [companyDeleteModal, setCompanyDeleteModal] = useState({ 
    show: false, 
    confirmText: '', 
    password: '', 
    loading: false,
    errors: { confirmText: '', password: '' }
  });
  
  // Final confirmation and success modal for company deletion
  const [finalCompanyDeleteModal, setFinalCompanyDeleteModal] = useState({
    show: false,
    step: 'confirm', // 'confirm', 'deleting', or 'success'
    countdown: 3
  });
  const [companyFormData, setCompanyFormData] = useState({});
  const [availablePackages, setAvailablePackages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downgradeConfirm, setDowngradeConfirm] = useState(null);
  const [upgradeConfirm, setUpgradeConfirm] = useState(null);
  const [billingInfo, setBillingInfo] = useState(null);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  const [baseCurrency, setBaseCurrency] = useState('');
  const [initialBaseCurrency, setInitialBaseCurrency] = useState(''); // Track initial value
  const [selectedExtraCurrencies, setSelectedExtraCurrencies] = useState([]);
  const [currencySearchTerm, setCurrencySearchTerm] = useState('');
  const [savingCurrencies, setSavingCurrencies] = useState(false);
  // Otomatik save için — ilk fetch tamamlanmadan useEffect tetiklemesin.
  const [currenciesHydrated, setCurrenciesHydrated] = useState(false);
  const [currenciesSavedAt, setCurrenciesSavedAt] = useState(null);
  
  // Profile edit states
  const [isEditMode, setIsEditMode] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
  });
  const [updating, setUpdating] = useState(false);

  // Initialize company form data
  useEffect(() => {
    console.log('User object in GeneralSettings:', user);
    console.log('User company:', user?.company);
    if (user?.company) {
      setCompanyFormData({
        name: user.company.company_name || user.company.name || '',
        description: user.company.description || '',
        address: user.company.address || '',
        city: user.company.city || '',
        postal_code: user.company.postal_code || '',
        tax_number: user.company.tax_number || '',
        tax_office: user.company.tax_office || '',
        phone: user.company.phone || '',
        website: user.company.website || '',
        industry: user.company.industry || '',
        employee_count: user.company.employee_count || '',
        contact_person: user.company.contact_person || '',
        allowed_currencies: user.company.allowed_currencies || []
      });
      
      // Load currency settings from new API
      loadCurrencySettings();
    }
  }, [user]);

  // Initialize profile data from user prop
  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        email: user.email || '',
      });
    }
  }, [user]);

  // Fetch currencies on mount
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/currencies/public`);
        const data = response.data;
        
        // Handle both old and new response formats
        const currenciesList = data.currencies || data;
        setCurrencies(currenciesList);
      } catch (error) {
        console.error('Error fetching currencies:', error);
        toast.error('Para birimleri yüklenirken hata oluştu');
      } finally {
        setLoadingCurrencies(false);
      }
    };

    fetchCurrencies();
  }, []);

  // Fetch available roles (excluding Admin and Super Admin)
  const fetchAvailableRoles = async () => {
    setLoadingRoles(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter out Admin and Super Admin roles
      const filteredRoles = response.data.filter(role => 
        role.name !== 'Admin' && 
        role.name !== 'Süper Admin' &&
        role.name !== 'admin' &&
        role.name !== 'Super Admin'
      );
      
      setAvailableRoles(filteredRoles);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Roller yüklenirken hata oluştu');
    } finally {
      setLoadingRoles(false);
    }
  };

  // Handle company form changes
  const handleCompanyFormChange = (field, value) => {
    setCompanyFormData(prev => ({ ...prev, [field]: value }));
  };

  // Save company information
  const handleSaveCompanyInfo = async () => {
    setLoading(true);
    try {
      // Prepare data - convert empty strings to null for integer fields
      const preparedData = {};
      for (const [key, value] of Object.entries(companyFormData)) {
        if (value === '' || value === null || value === undefined) {
          // Skip empty values - don't send them to backend
          continue;
        }
        
        // For employee_count, ensure it's a valid integer or null
        if (key === 'employee_count') {
          const parsed = parseInt(value, 10);
          if (!isNaN(parsed) && parsed > 0) {
            preparedData[key] = parsed;
          }
          // Skip if empty or invalid
        } else {
          preparedData[key] = value;
        }
      }
      
      console.log('📤 Sending company update:', preparedData);
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${BACKEND_URL}/api/company/update`,
        preparedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ Company update response:', response.data);
      
      toast.success('Şirket bilgileri başarıyla güncellendi');
      
      // Refresh user data without page reload
      await checkAuth();
      
    } catch (error) {
      console.error('Company update error:', error);
      console.error('Error response:', error.response?.data);
      
      // Handle different error formats
      let errorMessage = 'Güncelleme başarısız';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an object (Pydantic validation error)
        if (typeof detail === 'object' && detail.message) {
          errorMessage = detail.message;
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Handle array of validation errors
          errorMessage = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Load currency settings from API
  const loadCurrencySettings = async () => {
    if (!user?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const settings = await getCurrencySettings(user.id, token);
      
      console.log('📖 Loaded currency settings:', settings);
      
      setBaseCurrency(settings.baseCurrency);
      setInitialBaseCurrency(settings.baseCurrency); // Save initial value
      
      // Extract extras (all allowed except base)
      const extras = settings.allowedCurrencies.filter(c => c !== settings.baseCurrency);
      setSelectedExtraCurrencies(extras);
      // Success path — hydrated'ı de burada işaretle (finally sadece error'a takılıyor).
      setCurrenciesHydrated(true);
      return;
      
    } catch (error) {
      console.error('Error loading currency settings:', error);
      // Fallback to company settings if available
      const base = user.company?.base_currency || 'TRY';
      setBaseCurrency(base);
      setInitialBaseCurrency(base); // Save initial value
      
      const allowed = user.company?.allowed_currencies || [];
      const extras = allowed.filter(c => c !== base);
      setSelectedExtraCurrencies(extras);
    } finally {
      // Hydration tamamlandı — bundan sonra kullanıcının yaptığı değişiklikler
      // debounced auto-save ile backend'e yazılır.
      setCurrenciesHydrated(true);
    }
  };

  // Toggle extra currency
  const toggleExtraCurrency = (currencyCode) => {
    if (currencyCode === baseCurrency) {
      toast.error('Temel para biriminizi kaldıramazsınız');
      return;
    }
    
    if (selectedExtraCurrencies.includes(currencyCode)) {
      // Remove currency
      setSelectedExtraCurrencies(selectedExtraCurrencies.filter(c => c !== currencyCode));
    } else {
      // Add currency (max 4)
      if (selectedExtraCurrencies.length >= 4) {
        toast.error('Maksimum 4 ek para birimi seçebilirsiniz');
        return;
      }
      setSelectedExtraCurrencies([...selectedExtraCurrencies, currencyCode]);
    }
  };

  // Save currencies — otomatik veya manuel çağrılır. Otomatik modda `silent`
  // true olur: sadece küçük bir "Kaydedildi" göstergesi, sayfa yenilemesi yok.
  const saveCurrencies = async (silent = false) => {
    if (!user?.id) {
      if (!silent) toast.error('Kullanıcı bilgisi bulunamadı');
      return;
    }
    
    if (!baseCurrency) {
      if (!silent) toast.error('Lütfen temel para birimini seçin');
      return;
    }
    
    try {
      setSavingCurrencies(true);
      
      console.log('💾 Saving currencies:', {
        userId: user.id,
        baseCurrency: baseCurrency,
        extraCurrencies: selectedExtraCurrencies,
        silent,
      });
      
      const token = localStorage.getItem('token');
      if (!token) {
        if (!silent) toast.error('Oturum bulunamadı');
        return;
      }
      
      // Call new API
      const result = await updateCurrencySettings(
        user.id,
        baseCurrency,
        selectedExtraCurrencies,
        token
      );
      
      console.log('✅ Currency settings saved:', result);
      setCurrenciesSavedAt(new Date());
      
      if (silent) {
        // Sessiz mod — sadece görsel indicator; sayfa yenilemesi yok.
      } else {
        toast.success('✅ Para birimleri kaydedildi! Sayfa yenileniyor...');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
      
    } catch (error) {
      console.error('❌ Save currencies error:', error);
      
      const errorMsg = error.response?.data?.detail || 'Para birimleri kaydedilemedi';
      if (!silent) toast.error(errorMsg);
      
      setSavingCurrencies(false);
    } finally {
      // Not: try içi başarı yolu da setSavingCurrencies(false) ile bitmeli;
      // silent modda dahi UI göstergesini kapatalım.
      setSavingCurrencies(false);
    }
  };

  // Otomatik kayıt — kullanıcı Ayarlar > Uygulama Ayarları > Para Birimleri
  // bölümünde bir toggle veya base seçimi yaptığında `saveCurrencies` sessiz
  // modda çalışır. Debounce ~500ms ile hızlı ardışık tıklamalar tek istekte
  // birleşir. İlk hydrate'den önce tetiklenmez.
  useEffect(() => {
    if (!currenciesHydrated) return;
    if (!baseCurrency) return;
    const t = setTimeout(() => { saveCurrencies(true); }, 500);
    return () => clearTimeout(t);
  }, [baseCurrency, selectedExtraCurrencies, currenciesHydrated]);

  // Handle final confirmation for company deletion
  const handleFinalCompanyConfirmation = async () => {
    setFinalCompanyDeleteModal(prev => ({ ...prev, step: 'deleting' }));

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/auth/delete-my-company`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          data: { 
            confirmation_text: "şirketimi kalıcı olarak silmek istiyorum",
            password: companyDeleteModal.password 
          }
        }
      );

      // Close initial delete modal
      setCompanyDeleteModal({
        show: false,
        confirmText: '',
        password: '',
        loading: false,
        errors: { confirmText: '', password: '' }
      });

      // Show success message
      setFinalCompanyDeleteModal({ show: true, step: 'success', countdown: 3 });
      
      // Start countdown and logout after 3 seconds
      let timeLeft = 3;
      const countdownInterval = setInterval(() => {
        timeLeft -= 1;
        setFinalCompanyDeleteModal(prev => ({ ...prev, countdown: timeLeft }));
        
        if (timeLeft <= 0) {
          clearInterval(countdownInterval);
          
          // Clear storage and redirect
          localStorage.removeItem('token');
          window.location.href = '/';
        }
      }, 1000);

    } catch (error) {
      console.error('Delete company error:', error);
      const errorMsg = error.response?.data?.detail || 'Şirket silinirken hata oluştu';
      
      // Close final modal and reopen delete modal with error
      setFinalCompanyDeleteModal({ show: false, step: 'confirm', countdown: 3 });
      
      // Check if it's a field-specific error
      if (errorMsg.includes('Şifre hatalı')) {
        setCompanyDeleteModal(prev => ({ 
          ...prev, 
          show: true,
          loading: false,
          errors: { ...prev.errors, password: 'Şifre hatalı' }
        }));
      } else if (errorMsg.includes('Şirket adı')) {
        setCompanyDeleteModal(prev => ({ 
          ...prev,
          show: true, 
          loading: false,
          errors: { ...prev.errors, confirmText: errorMsg }
        }));
      } else {
        toast.error(errorMsg);
        setCompanyDeleteModal(prev => ({ ...prev, show: true, loading: false }));
      }
    }
  };

  // Load subscription packages
  const loadSubscriptionPackages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/packages`);
      setAvailablePackages(response.data.packages || {});
    } catch (error) {
      toast.error('Abonelik paketleri yüklenemedi');
    }
  };

  // Define plan hierarchy for upgrade/downgrade logic
  const planHierarchy = {
    'free_trial': { level: 0, price: 0 },
    'starter': { level: 1, price: 29 },
    'professional': { level: 2, price: 39 },
    'enterprise': { level: 3, price: 49 }
  };

  // Get current plan level
  const getCurrentPlanLevel = () => {
    const currentPlan = user?.trial_info?.is_trial ? 'free_trial' : user?.company?.subscription_plan;
    return planHierarchy[currentPlan]?.level || 0;
  };

  // Check if target plan is upgrade or downgrade
  const isUpgrade = (targetPlan) => {
    const currentLevel = getCurrentPlanLevel();
    const targetLevel = planHierarchy[targetPlan]?.level || 0;
    return targetLevel > currentLevel;
  };

  // Calculate billing info for upgrade
  const calculateBillingInfo = async (packageKey, actionType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/calculate-billing`,
        { package: packageKey, action: actionType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Billing calculation error:', error);
      return null;
    }
  };

  // Handle subscription change (upgrade or downgrade)
  const handleSubscriptionChange = async (packageKey, actionType) => {
    // Get billing information first
    const billing = await calculateBillingInfo(packageKey, actionType);
    
    if (actionType === 'upgrade') {
      setUpgradeConfirm({
        package: packageKey,
        packageName: availablePackages[packageKey]?.name,
        billing: billing
      });
    } else {
      setDowngradeConfirm({
        package: packageKey,
        packageName: availablePackages[packageKey]?.name,
        billing: billing
      });
    }
    
    setAgreementChecked(false); // Reset agreement
  };

  // Confirm upgrade
  const confirmUpgrade = async () => {
    if (!agreementChecked) {
      toast.error('Lütfen ödeme koşullarını kabul edin');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/upgrade-subscription`,
        { package: upgradeConfirm.package },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBillingInfo(response.data);
      setUpgradeConfirm(null);
      setIsSubscriptionModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yükseltme başarısız');
    } finally {
      setLoading(false);
    }
  };

  // Confirm downgrade
  const confirmDowngrade = async () => {
    if (!agreementChecked) {
      toast.error('Lütfen ödeme koşullarını kabul edin');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/downgrade-subscription`,
        { package: downgradeConfirm.package },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBillingInfo(response.data);
      setDowngradeConfirm(null);
      setIsSubscriptionModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Düşürme başarısız');
    } finally {
      setLoading(false);
    }
  };

  // Load invoices
  const loadInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/company/invoices`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvoices(response.data.invoices || []);
    } catch (error) {
      toast.error('Faturalar yüklenemedi');
    }
  };
  const closeCompanyDeleteModal = () => {
    setCompanyDeleteModal({ 
      show: false, 
      confirmText: '', 
      password: '', 
      loading: false,
      errors: { confirmText: '', password: '' }
    });
  };

  const handleProfileInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    if (!profileData.full_name.trim()) {
      toast.error('Ad Soyad alanı boş olamaz');
      return;
    }

    if (!profileData.email.trim()) {
      toast.error('E-posta alanı boş olamaz');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        full_name: profileData.full_name.trim(),
        email: profileData.email.trim(),
        role_id: user?.role_id,
        is_active: true
      };

      const response = await axios.put(
        `${API}/users/${user?.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh user data from auth context
      if (typeof checkAuth === 'function') {
        await checkAuth();
      }
      
      setIsEditMode(false);
      toast.success('Profil başarıyla güncellendi');
    } catch (error) {
      console.error('Profile update error:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('email')) {
        toast.error('Bu e-posta adresi zaten kullanımda');
      } else {
        toast.error('Profil güncellenirken hata oluştu');
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelProfileEdit = () => {
    setProfileData({
      full_name: user?.full_name || '',
      email: user?.email || '',
    });
    setIsEditMode(false);
  };

  const renderProfileSettings = () => {
    return (
      <>
        <div className="p-8">
          <div className="max-w-2xl">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Profil Ayarları</h3>
              <p className="text-gray-600 mt-2">Kişisel bilgilerinizi görüntüleyin ve güncelleyin</p>
            </div>
            
            <Card className="p-6">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                      <UserIcon className="w-8 h-8 text-teal-600" />
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900">{user?.full_name}</h4>
                      <p className="text-gray-600">{user?.email}</p>
                      <Badge className="mt-1">{user?.role?.name}</Badge>
                    </div>
                  </div>
                </div>
                
                {/* Warning Message */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start">
                  <AlertCircleIcon className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    Kullanıcı bilgilerindeki değişiklikler sadece Süper Admin tarafından yapılabilir
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="form-label">Ad Soyad</Label>
                    <Input 
                      value={user?.full_name || ''} 
                      className="form-input bg-gray-50"
                      readOnly
                      placeholder="Adınızı ve soyadınızı girin"
                    />
                  </div>
                  <div>
                    <Label className="form-label">E-posta</Label>
                    <Input 
                      value={user?.email || ''} 
                      className="form-input bg-gray-50"
                      readOnly
                      type="email"
                      placeholder="E-posta adresinizi girin"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Rol</Label>
                    <Input 
                      value={user?.role?.name || ''} 
                      className="form-input bg-gray-50"
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-1">Rol değişikliği için yöneticinize başvurun</p>
                  </div>
                  <div>
                    <Label className="form-label">Şirket</Label>
                    <Input 
                      value={user?.company?.company_name || user?.company?.name || ''} 
                      placeholder="Şirket adı belirtilmemiş"
                      className="form-input bg-gray-50"
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-1">Şirket bilgisi ayarlar → şirket bilgileri bölümünden güncellenebilir</p>
                  </div>
                </div>
                
                <div className="pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setChangeRequestData({
                        full_name: user?.full_name || '',
                        email: user?.email || '',
                        role_id: user?.role_id || ''
                      });
                      fetchAvailableRoles();
                      setIsChangeRequestModalOpen(true);
                    }}
                    className="text-teal-600 border-teal-600 hover:bg-teal-50"
                  >
                    <EditIcon className="w-4 h-4 mr-2" />
                    Değişiklik Talep Et
                  </Button>
                </div>
              </div>
            </Card>

            {/* Language Preference Card */}
            <Card className="p-6 mt-6" data-testid="language-preference-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <GlobeIcon className="w-6 h-6 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Uygulama Dili / Application Language
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Arayüz ve içerik dilini seçin. Bu tercih hesabınıza kaydedilir
                    ve tüm cihazlarınızda geçerli olur.
                  </p>

                  <div
                    className="mt-4 inline-flex p-1 bg-gray-100 rounded-lg"
                    role="group"
                    aria-label="Language selector"
                  >
                    <button
                      type="button"
                      onClick={() => handleSaveLanguage('tr')}
                      disabled={savingLanguage}
                      data-testid="language-toggle-tr"
                      className={`px-5 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
                        preferredLanguage === 'tr'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <span className="text-base">🇹🇷</span>
                      Türkçe
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSaveLanguage('en')}
                      disabled={savingLanguage}
                      data-testid="language-toggle-en"
                      className={`px-5 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
                        preferredLanguage === 'en'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <span className="text-base">🇬🇧</span>
                      English
                    </button>
                  </div>
                  {savingLanguage && (
                    <p className="text-xs text-gray-500 mt-2 flex items-center">
                      <Loader2Icon className="w-3 h-3 animate-spin mr-1" />
                      Kaydediliyor...
                    </p>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Change Request Modal */}
        {isChangeRequestModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="fixed inset-0 bg-black opacity-30" onClick={() => setIsChangeRequestModalOpen(false)}></div>
              
              <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 z-10">
                <h3 className="text-lg font-semibold mb-4">Profil Değişikliği Talep Et</h3>
                <p className="text-sm text-gray-600 mb-4">Yaptığınız değişiklikler Süper Admin onayından sonra geçerli olacaktır.</p>
                
                <div className="space-y-4">
                  <div>
                    <Label className="form-label">Ad Soyad</Label>
                    <Input
                      value={changeRequestData.full_name}
                      onChange={(e) => setChangeRequestData({...changeRequestData, full_name: e.target.value})}
                      className="form-input"
                      placeholder="Yeni ad soyad"
                    />
                  </div>
                  
                  <div>
                    <Label className="form-label">E-posta</Label>
                    <Input
                      type="email"
                      value={changeRequestData.email}
                      onChange={(e) => setChangeRequestData({...changeRequestData, email: e.target.value})}
                      className="form-input"
                      placeholder="Yeni e-posta"
                    />
                  </div>
                  
                  <div>
                    <Label className="form-label">Pozisyon</Label>
                    {loadingRoles ? (
                      <div className="flex items-center justify-center p-3 border rounded">
                        <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
                        <span className="text-sm text-gray-600">Pozisyonlar yükleniyor...</span>
                      </div>
                    ) : (
                      <select
                        value={changeRequestData.role_id}
                        onChange={(e) => setChangeRequestData({...changeRequestData, role_id: e.target.value})}
                        className="form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">Mevcut: {user?.role?.name || 'Pozisyon seçilmemiş'}</option>
                        {availableRoles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Admin ve Süper Admin pozisyonları değiştirilemez</p>
                  </div>
                  
                  <div>
                    <Label className="form-label">Şirket</Label>
                    <Input
                      value={user?.company?.company_name || user?.company?.name || ''}
                      placeholder="Şirket adı belirtilmemiş"
                      className="form-input bg-gray-100"
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-1">Şirket bilgisi ayarlar → şirket bilgileri bölümünden güncellenebilir</p>
                  </div>
                </div>
                
                <div className="flex space-x-3 mt-6">
                  <Button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        await axios.post(
                          `${API}/user-change-requests`,
                          changeRequestData,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        toast.success('Değişiklik talebiniz gönderildi. Süper Admin onayladığında bilgileriniz güncellenecektir.');
                        setIsChangeRequestModalOpen(false);
                      } catch (error) {
                        toast.error('Hata: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    className="btn-primary flex-1"
                  >
                    Talep Gönder
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsChangeRequestModalOpen(false)}
                    className="flex-1"
                  >
                    İptal
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderCompanySettings = () => {
    // Calculate profile completion percentage
    const calculateCompletionPercentage = () => {
      const company = user?.company;
      if (!company) return 0;
      
      const fields = [
        'name', 'address', 'city', 'postal_code', 'tax_number', 
        'tax_office', 'phone', 'website', 'industry', 'employee_count', 'contact_person'
      ];
      
      const completedFields = fields.filter(field => 
        company[field] && company[field].toString().trim() !== ''
      ).length;
      
      return Math.round((completedFields / fields.length) * 100);
    };

    const getMembershipDuration = () => {
      if (!user?.company?.created_at) return 'Bilinmiyor';
      
      const createdDate = new Date(user.company.created_at);
      const now = new Date();
      const diffTime = Math.abs(now - createdDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const diffMonths = Math.floor(diffDays / 30);
      const diffYears = Math.floor(diffMonths / 12);
      
      if (diffYears > 0) {
        const remainingMonths = diffMonths % 12;
        return `${diffYears} yıl ${remainingMonths > 0 ? remainingMonths + ' ay' : ''}`;
      } else if (diffMonths > 0) {
        return `${diffMonths} ay`;
      } else {
        return `${diffDays} gün`;
      }
    };

    const completionPercentage = calculateCompletionPercentage();
    const company = user?.company;
    
    return (
      <div className="p-8">
        <div className="max-w-4xl">
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Şirket Bilgileri</h3>
            <p className="text-gray-600 mt-2">Şirket profilinizi tamamlayın ve bilgilerinizi güncel tutun</p>
          </div>

          {/* Profile Completion Card */}
          <Card className="p-6 mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <BuildingIcon className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900">{company?.company_name || company?.name || 'Şirket Adı Belirtilmemiş'}</h4>
                  <p className="text-gray-600">{getMembershipDuration()} önce üye oldu</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <Badge variant={company?.is_active ? "default" : "secondary"}>
                      {company?.is_active ? 'Aktif' : 'Pasif'}
                    </Badge>
                    <Badge variant="outline">
                      {user?.trial_info?.is_trial ? 'Deneme Sürümü' : company?.subscription_plan || 'Plan Bilinmiyor'}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">{completionPercentage}%</div>
                <p className="text-sm text-gray-600">Profil Tamamlanma</p>
                <div className="w-20 bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${completionPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </Card>

          {/* Company Information */}
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <BuildingIcon className="w-5 h-5 text-gray-600" />
                <h4 className="text-lg font-semibold text-gray-900">Temel Şirket Bilgileri</h4>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="form-label">Şirket Adı *</Label>
                <Input 
                  value={companyFormData.name || ''} 
                  onChange={(e) => handleCompanyFormChange('name', e.target.value)}
                  className="form-input"
                  placeholder="Şirket adınızı girin"
                />
                {!companyFormData.name && <p className="text-red-500 text-xs mt-1">Bu alan zorunludur</p>}
              </div>
              
              <div>
                <Label className="form-label">Sektör</Label>
                <Input 
                  value={companyFormData.industry || ''} 
                  onChange={(e) => handleCompanyFormChange('industry', e.target.value)}
                  className="form-input"
                  placeholder="Örn: Teknoloji, Perakende, İmalat"
                />
              </div>

              <div>
                <Label className="form-label">İletişim E-postası *</Label>
                <Input 
                  value={company?.contact_email || company?.owner_email || ''} 
                  className="form-input bg-gray-50"
                  readOnly
                />
                <p className="text-gray-500 text-xs mt-1">E-posta değiştirilemez</p>
              </div>

              <div>
                <Label className="form-label">İletişim Sorumlusu</Label>
                <Input 
                  value={companyFormData.contact_person || ''} 
                  onChange={(e) => handleCompanyFormChange('contact_person', e.target.value)}
                  className="form-input"
                  placeholder="İletişim sorumlusu adı"
                />
              </div>

              <div>
                <Label className="form-label">Telefon</Label>
                <Input 
                  value={companyFormData.phone || ''} 
                  onChange={(e) => handleCompanyFormChange('phone', e.target.value)}
                  className="form-input"
                  placeholder="+90 (XXX) XXX XX XX"
                />
              </div>

              <div>
                <Label className="form-label">Web Sitesi</Label>
                <Input 
                  value={companyFormData.website || ''} 
                  onChange={(e) => handleCompanyFormChange('website', e.target.value)}
                  className="form-input"
                  placeholder="https://www.sirketiniz.com"
                />
              </div>

              <div>
                <Label className="form-label">Çalışan Sayısı</Label>
                <Input 
                  value={companyFormData.employee_count || ''} 
                  onChange={(e) => handleCompanyFormChange('employee_count', e.target.value)}
                  className="form-input"
                  placeholder="Örn: 25"
                  type="number"
                />
              </div>

              <div>
                <Label className="form-label">Temel Para Birimi *</Label>
                <div className="relative">
                  <Input 
                    value={baseCurrency ? `${baseCurrency} - ${currencies.find(c => c.code === baseCurrency)?.name || baseCurrency} (${currencies.find(c => c.code === baseCurrency)?.symbol || ''})`  : 'Yükleniyor...'}
                    className="form-input bg-gray-50 pr-10"
                    readOnly
                    disabled
                  />
                  <LockIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Temel para birimi üye olurken seçilir ve değiştirilemez.
                  <br />
                  <span className="text-blue-600 font-medium">💡 Avans için izin verilen para birimlerini aşağıdaki Para Birimi Ayarları bölümünden yönetin.</span>
                </p>
              </div>

              <div className="md:col-span-2">
                <Label className="form-label">Şirket Açıklaması</Label>
                <Input 
                  value={companyFormData.description || ''} 
                  onChange={(e) => handleCompanyFormChange('description', e.target.value)}
                  className="form-input"
                  placeholder="Şirket hakkında kısa açıklama"
                />
              </div>
            </div>
          </Card>

          {/* Address Information */}
          <Card className="p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <MapPinIcon className="w-5 h-5 text-gray-600" />
              <h4 className="text-lg font-semibold text-gray-900">Adres Bilgileri</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label className="form-label">Şirket Adresi</Label>
                <Input 
                  value={companyFormData.address || ''} 
                  onChange={(e) => handleCompanyFormChange('address', e.target.value)}
                  className="form-input"
                  placeholder="Tam adres bilgisi"
                />
              </div>

              <div>
                <Label className="form-label">Şehir</Label>
                <Input 
                  value={companyFormData.city || ''} 
                  onChange={(e) => handleCompanyFormChange('city', e.target.value)}
                  className="form-input"
                  placeholder="Şehir"
                />
              </div>

              <div>
                <Label className="form-label">Posta Kodu</Label>
                <Input 
                  value={companyFormData.postal_code || ''} 
                  onChange={(e) => handleCompanyFormChange('postal_code', e.target.value)}
                  className="form-input"
                  placeholder="34000"
                />
              </div>
            </div>
          </Card>

          {/* Tax Information */}
          <Card className="p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <CreditCardIcon className="w-5 h-5 text-gray-600" />
              <h4 className="text-lg font-semibold text-gray-900">Vergi Bilgileri</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="form-label">Vergi Numarası</Label>
                <Input 
                  value={companyFormData.tax_number || ''} 
                  onChange={(e) => handleCompanyFormChange('tax_number', e.target.value)}
                  className="form-input"
                  placeholder="Vergi numarası"
                />
              </div>

              <div>
                <Label className="form-label">Vergi Dairesi</Label>
                <Input 
                  value={companyFormData.tax_office || ''} 
                  onChange={(e) => handleCompanyFormChange('tax_office', e.target.value)}
                  className="form-input"
                  placeholder="Vergi dairesi adı"
                />
              </div>
            </div>
            
            {/* Save Button */}
            <div className="flex justify-end mt-6 pt-4 border-t">
              <Button 
                onClick={handleSaveCompanyInfo} 
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <SaveIcon className="w-4 h-4 mr-2" />
                    Bilgileri Kaydet
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Currency Management moved to Uygulama Ayarları > Para Birimleri */}

          {/* Subscription Information */}
          <Card className="p-6">
            <div className="flex items-center space-x-2 mb-6">
              <SettingsIcon className="w-5 h-5 text-gray-600" />
              <h4 className="text-lg font-semibold text-gray-900">Abonelik Bilgileri</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label className="form-label">Abonelik Planı</Label>
                <div className="mt-2">
                  <Badge className="text-sm">
                    {user?.trial_info?.is_trial ? 'Ücretsiz Deneme' : company?.subscription_plan || 'Plan Bilinmiyor'}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="form-label">Ödeme Durumu</Label>
                <div className="mt-2">
                  <Badge variant={company?.payment_status === 'paid' ? 'default' : 'secondary'}>
                    {company?.payment_status === 'paid' ? 'Ödendi' : 
                     company?.payment_status === 'trial' ? 'Deneme' : 
                     company?.payment_status === 'pending' ? 'Bekliyor' : 'Bilinmiyor'}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="form-label">Kullanıcı Limiti</Label>
                <div className="mt-2 text-sm text-gray-600">
                  {company?.user_limit || 0} kullanıcı
                </div>
              </div>

              <div>
                <Label className="form-label">Kayıt Tarihi</Label>
                <div className="mt-2 text-sm text-gray-600">
                  {company?.created_at ? new Date(company.created_at).toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'Bilinmiyor'}
                </div>
              </div>

              <div>
                <Label className="form-label">Abonelik Bitiş</Label>
                <div className="mt-2 text-sm text-gray-600">
                  {company?.subscription_end ? new Date(company.subscription_end).toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'Bilinmiyor'}
                </div>
              </div>

              <div>
                <Label className="form-label">Aylık Ücret</Label>
                <div className="mt-2 text-sm font-medium text-gray-900">
                  {company?.monthly_fee ? `₺${company.monthly_fee}` : '₺0 (Ücretsiz)'}
                </div>
              </div>
            </div>

            {/* Other Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-6 border-t border-gray-200">
              <Button 
                variant="outline"
                onClick={() => {
                  setIsSubscriptionModalOpen(true);
                  loadSubscriptionPackages();
                }}
              >
                <ArrowUpIcon className="w-4 h-4 mr-2" />
                Abonelik Yönetimi
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setIsInvoiceModalOpen(true);
                  loadInvoices();
                }}
              >
                <ReceiptIcon className="w-4 h-4 mr-2" />
                Fatura Geçmişi
              </Button>
            </div>
          </Card>

          {/* Missing Information Alert */}
          {completionPercentage < 100 && (
            <Card className="p-4 mt-6 bg-yellow-50 border-yellow-200">
              <div className="flex items-center space-x-2">
                <AlertCircleIcon className="w-5 h-5 text-yellow-600" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Profil Tamamlanmamış
                  </p>
                  <p className="text-sm text-yellow-700">
                    Şirket profilinizi %{100 - completionPercentage} daha tamamlayarak tüm özelliklerden faydalanabilirsiniz.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Subscription Management Modal */}
          {isSubscriptionModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">Abonelik Yönetimi</h3>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setIsSubscriptionModalOpen(false)}
                  >
                    <XIcon className="w-4 h-4" />
                  </Button>
                </div>

                {/* Current Plan */}
                <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-semibold text-blue-900">Mevcut Planınız</h4>
                      <p className="text-blue-700">
                        {user?.trial_info?.is_trial ? 'Ücretsiz Deneme' : user?.company?.subscription_plan || 'Plan Bilinmiyor'}
                      </p>
                      {user?.trial_info?.is_trial && (
                        <p className="text-sm text-blue-600 mt-1">
                          {user.trial_info.days_remaining} gün kaldı
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-900">
                        {user?.company?.monthly_fee ? `₺${user.company.monthly_fee}` : '₺0'}
                      </p>
                      <p className="text-sm text-blue-600">Aylık</p>
                    </div>
                  </div>
                </Card>

                {/* Available Packages */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {Object.entries(availablePackages).map(([key, pkg]) => {
                    const company = user?.company;
                    const isCurrentPlan = user?.trial_info?.is_trial ? key === 'free_trial' : company?.subscription_plan === key;
                    const currentLevel = getCurrentPlanLevel();
                    const targetLevel = planHierarchy[key]?.level || 0;
                    const isUpgradeOption = targetLevel > currentLevel;
                    const isDowngradeOption = targetLevel < currentLevel;
                    
                    return (
                      <Card key={key} className={`p-6 ${isCurrentPlan ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}>
                        <div className="text-center">
                          <h4 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h4>
                          <div className="text-3xl font-bold text-blue-600 mb-4">
                            ₺{pkg.price}
                            <span className="text-sm text-gray-500">/ay</span>
                          </div>
                          
                          {isCurrentPlan && (
                            <Badge className="mb-4 bg-blue-100 text-blue-800">
                              Mevcut Plan
                            </Badge>
                          )}

                          {isUpgradeOption && (
                            <Badge className="mb-4 bg-green-100 text-green-800">
                              <ArrowUpIcon className="w-3 h-3 mr-1" />
                              Yükseltme
                            </Badge>
                          )}

                          {isDowngradeOption && (
                            <Badge className="mb-4 bg-orange-100 text-orange-800">
                              <ArrowUpIcon className="w-3 h-3 mr-1 rotate-180" />
                              Düşürme
                            </Badge>
                          )}

                          <ul className="text-left space-y-2 mb-6">
                            {resolvePlanFeatures(pkg, {
                              featureIdToName: (id) => FEATURE_BY_ID[id]?.name,
                            }).map((feature, index) => (
                              <li key={index} className="flex items-center text-sm">
                                <CheckIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                                {feature}
                              </li>
                            ))}
                          </ul>

                          <div className="text-sm text-gray-600 mb-4">
                            <p>Kullanıcı Limiti: {pkg.user_limit}</p>
                            {pkg.trial_days && <p>Deneme Süresi: {pkg.trial_days} gün</p>}
                          </div>

                          {/* Price Comparison */}
                          {!isCurrentPlan && (
                            <div className="mb-4 p-2 bg-gray-50 rounded text-xs">
                              {isUpgradeOption && (
                                <p className="text-green-600">
                                  +₺{pkg.price - (company?.monthly_fee || 0)} artış
                                </p>
                              )}
                              {isDowngradeOption && (
                                <p className="text-orange-600">
                                  -₺{(company?.monthly_fee || 0) - pkg.price} azalış
                                </p>
                              )}
                            </div>
                          )}

                          {!isCurrentPlan && isUpgradeOption && (
                            <Button 
                              className="w-full bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleSubscriptionChange(key, 'upgrade')}
                              disabled={loading}
                            >
                              {loading ? 'İşleniyor...' : 
                               <span className="flex items-center justify-center">
                                 <ArrowUpIcon className="w-4 h-4 mr-2" />
                                 {pkg.price === 0 ? 'Denemeye Başla' : 'Yükselt'}
                               </span>}
                            </Button>
                          )}

                          {!isCurrentPlan && isDowngradeOption && (
                            <Button 
                              className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                              onClick={() => handleSubscriptionChange(key, 'downgrade')}
                              disabled={loading}
                            >
                              {loading ? 'İşleniyor...' : 
                               <span className="flex items-center justify-center">
                                 <ArrowUpIcon className="w-4 h-4 mr-2 rotate-180" />
                                 Düşür
                               </span>}
                            </Button>
                          )}

                          {isCurrentPlan && pkg.price > 0 && (
                            <Button variant="outline" className="w-full">
                              <SettingsIcon className="w-4 h-4 mr-2" />
                              Plan Ayarları
                            </Button>
                          )}

                          {isCurrentPlan && pkg.price === 0 && (
                            <div className="text-center text-sm text-gray-500">
                              Deneme sürümündesiniz
                            </div>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>

                {/* Billing Rules */}
                <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h5 className="font-semibold text-yellow-800 mb-3">📋 Ödeme Kuralları</h5>
                  <div className="text-sm text-yellow-700 space-y-2">
                    <div>
                      <p className="font-medium">🔼 Plan Yükseltme:</p>
                      <ul className="ml-4 mt-1 space-y-1">
                        <li>• Yeni özellikler hemen aktif olur</li>
                        <li>• Kalan günler için günlük fark hesaplanır ve tahsil edilir</li>
                        <li>• Ödeme kredi kartınızdan otomatik tahsil edilir</li>
                        <li>• Yenileme tarihiniz değişmez</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium">🔽 Plan Düşürme:</p>
                      <ul className="ml-4 mt-1 space-y-1">
                        <li>• Özellikler mevcut dönem sonunda değişir</li>
                        <li>• Ücret iadesi yapılmaz</li>
                        <li>• Yenileme tarihinde yeni fiyat uygulanır</li>
                        <li>• Kullanıcı limitiniz azalabilir</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium">💳 Ödeme Bilgileri:</p>
                      <ul className="ml-4 mt-1 space-y-1">
                        <li>• Tüm fiyatlar KDV dahildir</li>
                        <li>• Ödemeler TL cinsinden tahsil edilir</li>
                        <li>• Otomatik yenileme aktiftir</li>
                        <li>• İptal işlemi istediğiniz zaman yapabilirsiniz</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Plan Comparison */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2">Plan Karşılaştırması</h5>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>• <strong>Starter:</strong> Küçük işletmeler için temel özellikler</p>
                    <p>• <strong>Professional:</strong> Büyüyen şirketler için gelişmiş özellikler</p>
                    <p>• <strong>Enterprise:</strong> Kurumsal müşteriler için tam özellik seti</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Invoice History Modal */}
          {isInvoiceModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold brand-text-primary">Fatura Geçmişi</h3>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setIsInvoiceModalOpen(false)}
                  >
                    <XIcon className="w-4 h-4" />
                  </Button>
                </div>

                {invoices.length === 0 ? (
                  <div className="text-center py-12">
                    <ReceiptIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">Henüz fatura bulunmuyor</h4>
                    <p className="text-gray-500">İlk ödemeniz sonrasında faturalarınız burada görünecektir.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {invoices.map((invoice, index) => (
                      <Card key={index} className="p-4 hover:shadow-md transition-shadow" data-testid={`invoice-row-${index}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">
                              Fatura #{invoice.number}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {new Date(invoice.date).toLocaleDateString('tr-TR')}
                            </p>
                            <p className="text-sm brand-text-primary font-medium">
                              Plan: {invoice.plan_name}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-gray-900">
                              ₺{invoice.amount}
                            </p>
                            <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'}>
                              {invoice.status === 'paid' ? 'Ödendi' : 'Bekliyor'}
                            </Badge>
                          </div>
                          <div>
                            <Button variant="outline" size="sm" className="brand-border-primary brand-text-primary hover:opacity-80">
                              <ReceiptIcon className="w-4 h-4 mr-2" />
                              İndir
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Upgrade Confirmation Modal */}
          {upgradeConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ArrowUpIcon className="w-8 h-8 text-green-600" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Plan Yükseltme Onayı</h3>
                  
                  <p className="text-gray-600 mb-4">
                    Planınızı <strong>{upgradeConfirm.packageName}</strong> planına yükseltmek istediğinizden emin misiniz?
                  </p>

                  {/* Billing Details */}
                  {upgradeConfirm.billing && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-left">
                      <h4 className="font-medium text-green-800 mb-2">💳 Ödeme Detayları</h4>
                      <div className="text-sm text-green-700 space-y-1">
                        <p>• <strong>Yeni Aylık Ücret:</strong> ₺{upgradeConfirm.billing.newMonthlyFee}</p>
                        <p>• <strong>Kalan Gün Sayısı:</strong> {upgradeConfirm.billing.remainingDays} gün</p>
                        <p>• <strong>Günlük Fark:</strong> ₺{upgradeConfirm.billing.dailyDifference}</p>
                        <p>• <strong>Tahsil Edilecek Tutar:</strong> ₺{upgradeConfirm.billing.chargeAmount}</p>
                        <p>• <strong>Yenileme Tarihi:</strong> {upgradeConfirm.billing.renewalDate}</p>
                      </div>
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left">
                    <h4 className="font-medium text-blue-800 mb-2">✨ Hemen Aktif Olacak Özellikler:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Yeni plan özellikleri anında aktif</li>
                      <li>• Kullanıcı limitiniz artırılır</li>
                      <li>• Gelişmiş raporlama araçları</li>
                      <li>• Öncelikli destek</li>
                    </ul>
                  </div>

                  {/* Agreement Checkbox */}
                  <div className="flex items-start space-x-2 mb-6">
                    <input
                      type="checkbox"
                      id="upgrade-agreement"
                      checked={agreementChecked}
                      onChange={(e) => setAgreementChecked(e.target.checked)}
                      className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <label htmlFor="upgrade-agreement" className="text-sm text-gray-700">
                      Ödeme koşullarını okudum ve kabul ediyorum. Planımın hemen yükseltilmesini ve 
                      kalan günler için fark ücretinin kredi kartımdan tahsil edilmesini onaylıyorum.
                    </label>
                  </div>

                  <div className="flex space-x-3">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setUpgradeConfirm(null)}
                      disabled={loading}
                    >
                      İptal
                    </Button>
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      onClick={confirmUpgrade}
                      disabled={loading || !agreementChecked}
                    >
                      {loading ? 'İşleniyor...' : 'Onayla ve Yükselt'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Downgrade Confirmation Modal */}
          {downgradeConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ArrowUpIcon className="w-8 h-8 text-orange-600 rotate-180" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Plan Düşürme Onayı</h3>
                  
                  <p className="text-gray-600 mb-4">
                    Planınızı <strong>{downgradeConfirm.packageName}</strong> planına düşürmek istediğinizden emin misiniz?
                  </p>

                  {/* Billing Details */}
                  {downgradeConfirm.billing && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4 text-left">
                      <h4 className="font-medium text-orange-800 mb-2">💳 Ödeme Detayları</h4>
                      <div className="text-sm text-orange-700 space-y-1">
                        <p>• <strong>Yeni Aylık Ücret:</strong> ₺{downgradeConfirm.billing.newMonthlyFee}</p>
                        <p>• <strong>Değişiklik Tarihi:</strong> {downgradeConfirm.billing.effectiveDate}</p>
                        <p>• <strong>Yenileme Tarihi:</strong> {downgradeConfirm.billing.renewalDate}</p>
                        <p>• <strong>Ücret İadesi:</strong> Yapılmayacak</p>
                      </div>
                    </div>
                  )}

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 text-left">
                    <h4 className="font-medium text-yellow-800 mb-2">⚠️ Önemli Bilgiler:</h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• Özellikler mevcut dönem sonunda değişecek</li>
                      <li>• Kullanıcı limitiniz azalabilir</li>
                      <li>• Gelişmiş özellikler kısıtlanacak</li>
                      <li>• Ücret iadesi yapılmayacak</li>
                      <li>• İsterseniz tekrar yükseltebilirsiniz</li>
                    </ul>
                  </div>

                  {/* Agreement Checkbox */}
                  <div className="flex items-start space-x-2 mb-6">
                    <input
                      type="checkbox"
                      id="downgrade-agreement"
                      checked={agreementChecked}
                      onChange={(e) => setAgreementChecked(e.target.checked)}
                      className="mt-1 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <label htmlFor="downgrade-agreement" className="text-sm text-gray-700">
                      Ödeme koşullarını okudum ve kabul ediyorum. Planımın dönem sonunda düşürülmesini,
                      özellik kısıtlamalarını ve ücret iadesi yapılmayacağını onaylıyorum.
                    </label>
                  </div>

                  <div className="flex space-x-3">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setDowngradeConfirm(null)}
                      disabled={loading}
                    >
                      İptal
                    </Button>
                    <Button 
                      className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                      onClick={confirmDowngrade}
                      disabled={loading || !agreementChecked}
                    >
                      {loading ? 'İşleniyor...' : 'Onayla ve Düşür'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Billing Info Modal (Success) */}
          {billingInfo && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircleIcon className="w-8 h-8 text-green-600" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">İşlem Başarılı!</h3>
                  
                  <p className="text-gray-600 mb-4">
                    {billingInfo.message}
                  </p>

                  {billingInfo.chargeAmount && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-green-700">
                        <strong>Tahsil Edilen Tutar:</strong> ₺{billingInfo.chargeAmount}
                      </p>
                    </div>
                  )}

                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => {
                      setBillingInfo(null);
                      // Return to company settings page
                      window.location.href = '/dashboard/settings?tab=company';
                    }}
                  >
                    Şirket Ayarlarına Dön
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Company Deletion Section - Only for Admin/Süper Admin, NOT for Master Templates */}
          {(user?.role?.name === 'Admin' || user?.role?.name === 'Süper Admin') && !user?.company?.is_master_template && (
            <Card className="p-6 border-red-200 bg-red-50 mt-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircleIcon className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-red-900">Tehlikeli Bölge</h4>
                  <p className="text-sm text-red-700">Bu işlemler geri alınamaz</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-red-200">
                <div className="flex items-start space-x-4">
                  <AlertCircleIcon className="w-6 h-6 text-red-500 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900 mb-2">Şirketimi Kalıcı Olarak Sil</h5>
                    <p className="text-sm text-gray-600 mb-4">
                      Şirketinizi sildiğinizde, tüm verileriniz kalıcı olarak silinir ve bu işlem geri alınamaz. 
                      Tüm kullanıcılar, avans kayıtları, raporlar ve diğer veriler tamamen silinecektir.
                    </p>
                    
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                      <div className="flex items-start space-x-2">
                        <AlertCircleIcon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-800">
                          <p className="font-medium mb-1">Şirket silme kuralları:</p>
                          <ul className="space-y-1">
                            <li>• Sadece Admin veya Süper Admin şirketi silebilir</li>
                            <li>• Şirkette başka aktif kullanıcı varsa silme işlemi yapılamaz</li>
                            <li>• Önce tüm kullanıcıları silmeniz gerekir</li>
                            <li>• Tüm veriler kalıcı olarak silinir (avanslar, raporlar, vb.)</li>
                            <li>• Bu işlem geri alınamaz!</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={() => setCompanyDeleteModal({ show: true, confirmText: '', password: '', loading: false })}
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <AlertCircleIcon className="w-4 h-4 mr-2" />
                      Şirketimi Sil
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}
          
          {/* Master Template Protection Notice for Company Deletion */}
          {(user?.role?.name === 'Admin' || user?.role?.name === 'Süper Admin') && user?.company?.is_master_template && (
            <Card className="p-6 border-blue-200 bg-blue-50 mt-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ShieldIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-blue-900">Korumalı Master Şirket</h4>
                  <p className="text-sm text-blue-700">Bu şirket sistem tarafından korunmaktadır</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <div className="flex items-start space-x-4">
                  <InfoIcon className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900 mb-2">Master Template Şirketi</h5>
                    <p className="text-sm text-gray-600 mb-4">
                      Bu şirket, paket özelliklerinin şablon olarak kullanıldığı bir master şirkettir. 
                      Sistem bütünlüğü için bu şirket silinemez ve süresiz kullanımdadır.
                    </p>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <CheckCircleIcon className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-blue-800">
                          <p className="font-medium mb-1">Master şirket özellikleri:</p>
                          <ul className="space-y-1">
                            <li>• Süresiz kullanım hakkı</li>
                            <li>• Şirket silinemez</li>
                            <li>• Tüm özelliklere erişim</li>
                            <li>• Yeni kayıtlar için şablon olarak kullanılır</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Company Deletion Modal */}
          {companyDeleteModal.show && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircleIcon className="w-8 h-8 text-red-600" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Şirketi Kalıcı Olarak Sil</h3>
                  
                  <p className="text-gray-600 mb-6">
                    Bu işlem <strong>geri alınamaz</strong>. Tüm verileriniz kalıcı olarak silinecektir.
                  </p>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                    <h4 className="font-medium text-red-800 mb-2">⚠️ Silinecek Veriler:</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• Tüm kullanıcı hesapları</li>
                      <li>• Tüm avans kayıtları</li>
                      <li>• Tüm raporlar ve belgeler</li>
                      <li>• Şirket ayarları ve konfigürasyonları</li>
                      <li>• Ödeme geçmişi ve faturalar</li>
                    </ul>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Onaylamak için şirket adını yazın: <strong>{user?.company?.name}</strong>
                      </label>
                      <Input
                        type="text"
                        value={companyDeleteModal.confirmText}
                        onChange={(e) => setCompanyDeleteModal(prev => ({ 
                          ...prev, 
                          confirmText: e.target.value,
                          errors: { ...prev.errors, confirmText: '' }
                        }))}
                        placeholder="Şirket adını buraya yazın"
                        className={`w-full ${companyDeleteModal.errors.confirmText ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                      />
                      {companyDeleteModal.errors.confirmText && (
                        <p className="mt-1 text-xs text-red-600">
                          {companyDeleteModal.errors.confirmText}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Güvenlik için şifrenizi girin:
                      </label>
                      <Input
                        type="password"
                        value={companyDeleteModal.password}
                        onChange={(e) => setCompanyDeleteModal(prev => ({ 
                          ...prev, 
                          password: e.target.value,
                          errors: { ...prev.errors, password: '' }
                        }))}
                        placeholder="Mevcut şifreniz"
                        className={`w-full ${companyDeleteModal.errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                      />
                      {companyDeleteModal.errors.password && (
                        <p className="mt-1 text-xs text-red-600">
                          {companyDeleteModal.errors.password}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setCompanyDeleteModal({ show: false, confirmText: '', password: '', loading: false, errors: { confirmText: '', password: '' } })}
                      disabled={companyDeleteModal.loading}
                    >
                      İptal
                    </Button>
                    <Button 
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => {
                        // Validate first
                        let hasErrors = false;
                        const newErrors = { confirmText: '', password: '' };

                        if (companyDeleteModal.confirmText !== user?.company?.name) {
                          newErrors.confirmText = 'Şirket adı doğru yazılmalı';
                          hasErrors = true;
                        }
                        if (!companyDeleteModal.password.trim()) {
                          newErrors.password = 'Şifre gerekli';
                          hasErrors = true;
                        }

                        if (hasErrors) {
                          setCompanyDeleteModal(prev => ({ ...prev, errors: newErrors }));
                          return;
                        }

                        // Show final confirmation modal
                        setFinalCompanyDeleteModal({ show: true, step: 'confirm', countdown: 3 });
                      }}
                      disabled={companyDeleteModal.loading || companyDeleteModal.confirmText !== user?.company?.name || !companyDeleteModal.password.trim()}
                    >
                      {companyDeleteModal.loading ? (
                        <>
                          <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                          Siliniyor...
                        </>
                      ) : (
                        <>
                          <AlertCircleIcon className="w-4 h-4 mr-2" />
                          İleri
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Final Confirmation/Success Modal for Company Deletion */}
          {finalCompanyDeleteModal.show && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                
                {/* Final Confirmation Step */}
                {finalCompanyDeleteModal.step === 'confirm' && (
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                      <AlertCircleIcon className="w-8 h-8 text-red-600" />
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      Emin misiniz?
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                      Şirketinizi <strong>kalıcı olarak silmek</strong> üzeresiniz. 
                      Bu işlem <strong>geri alınamaz</strong> ve tüm verileriniz tamamen silinecek.
                      <br/><br/>
                      Tüm kullanıcılar, avanslar, raporlar ve diğer veriler kalıcı olarak silinecektir.
                      <br/><br/>
                      Gerçekten devam etmek istiyor musunuz?
                    </p>

                    <div className="flex space-x-3">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setFinalCompanyDeleteModal({ show: false, step: 'confirm', countdown: 3 })}
                      >
                        Hayır, İptal Et
                      </Button>
                      <Button
                        onClick={handleFinalCompanyConfirmation}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      >
                        Evet, Sil
                      </Button>
                    </div>
                  </div>
                )}

                {/* Deleting Step */}
                {finalCompanyDeleteModal.step === 'deleting' && (
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                      <Loader2Icon className="w-8 h-8 text-blue-600 animate-spin" />
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Şirket Siliniyor...
                    </h3>
                    
                    <p className="text-gray-600 text-sm">
                      Lütfen bekleyin, şirketiniz siliniyor.
                    </p>
                  </div>
                )}

                {/* Success Step */}
                {finalCompanyDeleteModal.step === 'success' && (
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircleIcon className="w-8 h-8 text-green-600" />
                    </div>
                    
                    <h3 className="text-xl font-bold text-green-900 mb-2">
                      Şirket Silinmiştir
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-4">
                      Şirketiniz başarıyla silindi. Tüm veriler kalıcı olarak temizlendi.
                    </p>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <strong>{finalCompanyDeleteModal.countdown}</strong> saniye sonra ana sayfaya yönlendirileceksiniz...
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderComingSoon = (title, description, icon) => (
    <div className="p-8">
      <div className="max-w-4xl">
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
          <p className="text-gray-600 mt-2">{description}</p>
        </div>
        
        <Card className="p-8 text-center">
          {icon}
          <h4 className="text-xl font-semibold text-gray-900 mb-2">Yakında Geliyor</h4>
          <p className="text-gray-600">
            {title} özellikleri geliştirilme aşamasında.
          </p>
        </Card>
      </div>
    </div>
  );

  // Para Birimleri Yönetimi — Şirket Bilgileri altından çıkarıldı, artık
  // Uygulama Ayarları altında bağımsız bir kategori olarak sunuluyor.
  const renderCurrencyManagementSection = () => (
    <div className="space-y-6 max-w-4xl mx-auto p-6">
          {/* Currency Management */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <CreditCardIcon className="w-5 h-5 text-gray-600" />
                <h4 className="text-lg font-semibold text-gray-900">Para Birimleri Yönetimi</h4>
              </div>
              {/* Auto-save status — seçim yapıldığı anda arka planda kaydeder,
                  ayrı bir Kaydet butonuna gerek yok. */}
              <div className="flex items-center text-sm" data-testid="currency-autosave-status">
                {savingCurrencies ? (
                  <span className="flex items-center text-emerald-700">
                    <Loader2Icon className="w-4 h-4 mr-1.5 animate-spin" />
                    Kaydediliyor...
                  </span>
                ) : currenciesSavedAt ? (
                  <span className="flex items-center text-emerald-700">
                    <CheckIcon className="w-4 h-4 mr-1.5" />
                    Otomatik kaydedildi
                  </span>
                ) : (
                  <span className="text-gray-400 text-xs">Değişiklikler otomatik kaydedilir</span>
                )}
              </div>
            </div>

            <div className="space-y-6">
              {/* Base Currency Selection */}
              <div>
                <Label className="form-label font-semibold">
                  Temel Para Birimi <span className="text-red-500">*</span>
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Tüm hesaplamalar bu para birimine göre yapılır. Her zaman aktiftir.
                </p>
                
                {initialBaseCurrency ? (
                  // Already set - show as read-only badge
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl">
                          {currencies.find(c => c.code === baseCurrency)?.symbol || baseCurrency}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-blue-900">
                            {baseCurrency}
                          </span>
                          <Badge className="bg-blue-600 text-white">Temel</Badge>
                        </div>
                        <p className="text-sm text-blue-700 mt-1">
                          {currencies.find(c => c.code === baseCurrency)?.name || baseCurrency}
                        </p>
                      </div>
                      <LockIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    
                    <div className="flex items-start space-x-2 bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <AlertCircleIcon className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-orange-800">
                        <p className="font-semibold mb-1">⚠️ Temel para birimi değiştirilemez</p>
                        <p>Tüm geçmiş avanslar, limitler ve istatistikler bu para birimine göre hesaplanmıştır. Değişiklik veri tutarsızlığına yol açar.</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Not set yet - show dropdown
                  <select
                    value={baseCurrency}
                    onChange={(e) => setBaseCurrency(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    disabled={savingCurrencies}
                  >
                    <option value="">Para birimi seçin</option>
                    {loadingCurrencies ? (
                      <option disabled>Yükleniyor...</option>
                    ) : (
                      currencies.map(currency => (
                        <option key={currency.code} value={currency.code}>
                          {currency.symbol} {currency.code} - {currency.name}
                        </option>
                      ))
                    )}
                  </select>
                )}
              </div>

              {/* Extra Currencies Selection */}
              <div>
                <Label className="form-label font-semibold">
                  Ek Para Birimleri (Maksimum 4)
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Avans talep ederken kullanabileceğiniz ek para birimleri. Otomatik kur dönüşümü yapılır.
                </p>

                {/* Search Bar */}
                <div className="relative mb-4">
                  <input
                    type="text"
                    placeholder="Para birimi ara... (örn: USD, Euro, Dolar)"
                    value={currencySearchTerm}
                    onChange={(e) => setCurrencySearchTerm(e.target.value)}
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    disabled={savingCurrencies}
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>

                {/* Selected Extra Currencies */}
                <div className="flex flex-wrap gap-2 min-h-[60px] p-3 border border-gray-300 rounded-lg bg-gray-50 mb-4">
                  {baseCurrency && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700 border border-blue-300">
                      {currencies.find(c => c.code === baseCurrency)?.symbol || ''} {baseCurrency}
                      <span className="ml-2 text-xs">(Temel)</span>
                    </span>
                  )}
                  {selectedExtraCurrencies.length === 0 ? (
                    <span className="text-sm text-gray-500">Henüz ek para birimi seçilmedi</span>
                  ) : (
                    selectedExtraCurrencies.map((code) => {
                      const currency = currencies.find(c => c.code === code);
                      return (
                        <span
                          key={code}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700"
                        >
                          {currency?.symbol || ''} {code}
                          <button
                            onClick={() => toggleExtraCurrency(code)}
                            className="ml-2 text-emerald-600 hover:text-emerald-800"
                            disabled={savingCurrencies}
                          >
                            <XIcon className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })
                  )}
                </div>

                {/* Counter */}
                <div className="flex items-center justify-between text-sm mb-4">
                  <span className="text-gray-600">
                    Seçili: {selectedExtraCurrencies.length} / 4 ek para birimi
                  </span>
                  {selectedExtraCurrencies.length >= 4 && (
                    <span className="text-orange-600 font-medium">
                      ⚠️ Maksimum limit doldu
                    </span>
                  )}
                </div>

                {/* Currency Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[300px] overflow-y-auto p-2 border border-gray-200 rounded-lg bg-white">
                  {loadingCurrencies ? (
                    <div className="col-span-full text-center py-8">
                      <Loader2Icon className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                      <p className="text-sm text-gray-500 mt-2">Yükleniyor...</p>
                    </div>
                  ) : (
                    (() => {
                      // Filter currencies by search term
                      const filteredCurrencies = currencies.filter(currency => {
                        if (!currencySearchTerm) return true;
                        const searchLower = currencySearchTerm.toLowerCase();
                        return (
                          currency.code.toLowerCase().includes(searchLower) ||
                          (currency.name && currency.name.toLowerCase().includes(searchLower)) ||
                          (currency.symbol && currency.symbol.includes(searchLower))
                        );
                      });

                      if (filteredCurrencies.length === 0) {
                        return (
                          <div className="col-span-full text-center py-8 text-gray-500">
                            <p>&quot;{currencySearchTerm}&quot; için sonuç bulunamadı</p>
                          </div>
                        );
                      }

                      return filteredCurrencies.map((currency) => {
                        const isBase = currency.code === baseCurrency;
                        const isSelected = selectedExtraCurrencies.includes(currency.code);
                        return (
                          <button
                            key={currency.code}
                            type="button"
                            onClick={() => toggleExtraCurrency(currency.code)}
                            disabled={isBase || savingCurrencies}
                            className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-colors ${
                              isSelected
                                ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                                : isBase
                                ? 'bg-blue-50 border-blue-300 text-blue-700 cursor-not-allowed'
                                : 'bg-white border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                            }`}
                          >
                            <div className="flex items-center space-x-2">
                              <span className="text-lg">{currency.symbol}</span>
                              <span className="text-sm font-medium">{currency.code}</span>
                            </div>
                            {isSelected && !isBase && (
                              <CheckIcon className="w-4 h-4 text-emerald-600" />
                            )}
                            {isBase && (
                              <span className="text-xs">(Temel)</span>
                            )}
                          </button>
                        );
                      });
                    })()
                  )}
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Nasıl Çalışır?</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Temel para biriminiz her zaman aktiftir</li>
                      <li>İstediğiniz 4 ek para birimini seçebilirsiniz (toplam 5)</li>
                      <li>Avans talep ederken sadece seçili para birimlerini kullanabilirsiniz</li>
                      <li>Tüm tutarlar otomatik olarak temel para biriminize çevrilir</li>
                      <li>Seçimler otomatik olarak kaydedilir — ayrı bir kaydet butonu yok</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Card>
    </div>
  );

  switch (type) {
    case 'profile':
      return renderProfileSettings();
    case 'company':
      return renderCompanySettings();
    case 'app-currencies':
      return renderCurrencyManagementSection();
    case 'categories':
      return renderComingSoon(
        'Kategori Yönetimi',
        'Avans kategorilerini yönetin ve düzenleyin',
        <SettingsIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
      );
    case 'projects':
      return renderComingSoon(
        'Proje Yönetimi',
        'Proje listelerini yönetin ve düzenleyin',
        <CreditCardIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
      );
    default:
      return renderProfileSettings();
  }
};

export default GeneralSettings;